import { Block } from "@components/block";
import { Breadcrumbs } from "@components/breadcrumbs";
import { EllipsisText } from "@components/ellipsis-text";
import { StatusBlock, StatusBlockType } from "@components/status-block";
import { Config, DoubleText, Table } from "@components/table";
import { usePayoutOrders } from "@features/payouts";
import { OrderPayout, OrderPayoutRequest } from "@features/payouts/api";
import { formatDate, formatTime } from "@helpers/date";
import { formatRubles, formatUsdt } from "@helpers/format";
import { usePagination } from "@hooks/use-pagination";
import { useEffect, useState } from "react";
import TickSquare from "@assets/icons/tick-square.svg";
import CloseSquare from "@assets/icons/close-square.svg";
import { useBoolState } from "@hooks/use-bool-state";
import { PayOutOrderModal } from "./modal";
import { getBanks } from "@features/bank/api";
import styles from "./payout.module.scss";
import { SelectOption } from "@components/select";
import { FilterConfig } from "@components/filter-modal";
import { useUrlFilters } from "@hooks/useUrlFilters";
import { WebsocketUpdateType } from "@request/websocket";
import { useWebSocket } from "@request/useWebsocket";
import { PayOutCancelModal } from "./cancelModal";

export const TraderWithdrawals = () => {
  const { state, getPayoutOrders } = usePayoutOrders();
  const { page, limit, setPage, setLimit } = usePagination(100);
  const [order, setOrder] = useState<OrderPayout | null>(null);
  const [isView, setIsView] = useState<boolean>(false);
  const [isOpen, open, close] = useBoolState(false);
  const [isOpenCancel, openCancel, closeCancel] = useBoolState(false);
  const [bankOptions, setBankOptions] = useState<SelectOption[]>([]);
  const { setParams } = useUrlFilters();
  const { lastMessage } = useWebSocket();

  useEffect(() => {
    if (lastMessage && lastMessage.type == WebsocketUpdateType.PayOut) {
      getPayoutOrders({ page, limit, my_orders: true });
    }
  }, [lastMessage, page, limit]);

  useEffect(() => {
    getBanks().then((banks) => {
      const options = banks.map((bank) => ({
        text: bank.name,
        value: bank.id,
      }));

      setBankOptions(options);
    });
  }, []);
  const config: Config<OrderPayout> = [
    { label: "Способ", source: "method" },
    { label: "Банк", source: "bank" },
    {
      label: "Номер заявки",
      source: "id",
      renderComponent: (val) => <EllipsisText text={val} copy />,
    },
    {
      label: "Статус",
      source: "status",
      renderComponent: (data) => {
        let text = "";
        let status: StatusBlockType = StatusBlockType.Error;
        switch (data) {
          case "completed":
            text = "Успешно";
            status = StatusBlockType.Success;
            break;
          case "dispute_open":
            text = "Открыт спор";
            status = StatusBlockType.Orange;
            break;
          case "processing":
          case "pending":
          case "assigned_to_trader":
            text = "Ожидание";
            status = StatusBlockType.Warning;
            break;
          case "under_review_by_support":
            text = "На рассмотрении";
            status = StatusBlockType.Warning;
            break;
          case "failed":
          case "canceled":
            text = "Отклонена";
            status = StatusBlockType.Error;
            break;
        }
        return <StatusBlock text={text} type={status} />;
      },
    },
    {
      label: "Реквизиты",
      source: "requisite",
      renderComponent: (val) => <EllipsisText text={val} copy />,
    },
    {
      label: "Сумма",
      source: "sum",
      renderComponent: (data, item) => (
        <DoubleText
          text={formatRubles(data)}
          subText={formatUsdt(item.sum_usdt)}
        />
      ),
    },
    {
      label: "Прибыль",
      source: "ern_usdt",
      renderComponent: (data, item) => <DoubleText text={formatRubles(item.ern_rub)} subText={formatUsdt(data)}/>,
    },
    {
      label: "Дата закрытия",
      source: "closed_time_unix",
      renderComponent: (time) => {
        return (
          time && (
            <DoubleText
              text={formatTime(new Date(time * 1000))}
              subText={formatDate(new Date(time * 1000))}
            />
          )
        );
      },
    },
    {
      label: "Дата и время",
      source: "created_at_unix",
      renderComponent: (time) => {
        return (
          time && (
            <DoubleText
              text={formatTime(new Date(time * 1000))}
              subText={formatDate(new Date(time * 1000))}
            />
          )
        );
      },
    },
    {
      label: "",
      source: "ern_usdt",
      renderComponent: (_, item) => {
        if (item.status == "assigned_to_trader")
          return (
            <div style={{ display: "flex" }}>
              <TickSquare
                className="w-[28px] h-[28px]"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setIsView(false);
                  setOrder(item);
                  open();
                }}
              />
              <CloseSquare
                className="w-[28px] h-[28px]"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setOrder(item);
                  openCancel();
                  // cancelPayOutOrder(item.id).then(() => {
                  //   getPayoutOrders({ limit, page, my_orders: true });
                  // });
                }}
              />
            </div>
          );
        else
          return (
            <div
              style={{ textDecoration: "underline", cursor: "pointer" }}
              onClick={() => {
                setIsView(true);
                setOrder(item);
                open();
              }}
            >
              Посмотреть
            </div>
          );
      },
    },
  ];

  const statusOptions = [
    { text: "Ожидание", value: "assigned_to_trader" },
    { text: "На рассмотрении", value: "under_review_by_support" },
    { text: "Успешно", value: "completed" },
    { text: "Отменена", value: "canceled" },
    { text: "Спор открыт", value: "dispute_open" },
  ];
  const methodOptions: SelectOption[] = [
    { text: "СБП", value: "sbp" },
    { text: "Карта", value: "card" },
    { text: "Счет", value: "account" },
  ];
  const filterConfig: FilterConfig<OrderPayoutRequest> = [
    {
      type: "select",
      options: methodOptions,
      name: "method",
      label: "Метод",
    },
    {
      type: "select",
      options: statusOptions,
      name: "status",
      label: "Статус",
    },
    {
      type: "sum",
      minName: "min_sum",
      maxName: "max_sum",
    },
    {
      type: "select",
      options: bankOptions,
      name: "bank",
      label: "Банк",
    },
    {
      type: "date",
      withTime: true,
      nameStart: "date_from",
      nameEnd: "date_to",
      returnValue: "number",
    },
  ];

  useEffect(() => {
    getPayoutOrders({ limit, page, my_orders: true });
  }, [getPayoutOrders, limit, page]);

  const onFilterSubmit = (data: OrderPayoutRequest) => {
    setParams(data);
    getPayoutOrders({ ...data, my_orders: true });
  };
  return (
    <>
      <Breadcrumbs items={["Выводы", "Мои заявки на вывод"]} />
      <Block>
        <Table
          className={styles.tableStuff}
          title="Мои заявки на вывод"
          config={config}
          loading={state.loading}
          data={state.payoutOrders}
          withFilter
          filterProps={{
            filterConfig,
            // validationRules: validationRules,
            onSubmit: onFilterSubmit,
          }}
          // data={[data]}
          setPage={setPage}
          setLimit={setLimit}
          page={page}
          limit={limit}
          limits={[100, 200, 300]}
          totalCount={state.totalCount}
          cellClassName={styles.cell}
        ></Table>
      </Block>
      {order && (
        <PayOutOrderModal
          isOpen={isOpen}
          hide={close}
          data={order}
          isView={isView}
        />
      )}
      {order && (
        <PayOutCancelModal
          isOpen={isOpenCancel}
          hide={closeCancel}
          data={order}
        />
      )}
    </>
  );
};
